/* eslint-disable max-lines-per-function */
import type { JSX } from 'react';
import { useState } from 'react';
import { MoreHorizontal, Pencil, Trash2, Archive, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { JobPosition } from '../types/jobPosition.types';
import { ConfirmDialog } from './ConfirmDialog';

export interface JobPositionActionsProps {
  jobPosition: JobPosition;
  onEdit: (jobPosition: JobPosition) => void;
  onDelete: (id: string) => void;
  onArchive: (id: string, archive: boolean) => void;
}

export function JobPositionActions({
  jobPosition,
  onEdit,
  onDelete,
  onArchive,
}: JobPositionActionsProps): JSX.Element {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const isArchived = jobPosition.status === 'ARCHIVED';

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" aria-label="Actions">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(jobPosition)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => setShowArchiveConfirm(true)}>
            {isArchived ? (
              <ArchiveRestore className="mr-2 h-4 w-4" />
            ) : (
              <Archive className="mr-2 h-4 w-4" />
            )}
            {isArchived ? 'Unarchive' : 'Archive'}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onClick={() => setShowDeleteConfirm(true)}>
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Delete Job Position"
        description={`Are you sure you want to delete "${jobPosition.title}"? This action cannot be undone.`}
        onConfirm={() => onDelete(jobPosition.id)}
        confirmLabel="Delete"
      />

      <ConfirmDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        title={`${isArchived ? 'Unarchive' : 'Archive'} Job Position`}
        description={`Are you sure you want to ${isArchived ? 'unarchive' : 'archive'} "${jobPosition.title}"?`}
        onConfirm={() => onArchive(jobPosition.id, !jobPosition.isManuallyArchived)}
      />
    </>
  );
}
